import API from "../axiosConfig";

export const searchSubscriptions = async (params) => {
  try {
    const response = await API.get("/subscriptions", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    throw error;
  }
};

export const getSubscriptionById = async (id) => {
  try {
    const response = await API.get(`/subscriptions/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription:", error);
    throw error;
  }
};

export const createSubscription = async (data) => {
  try {
    const response = await API.post("/subscriptions", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating subscription:", error);
    throw error;
  }
};

export const updateSubscription = async (id, data) => {
  try {
    const response = await API.put(`/subscriptions/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating subscription:", error);
    throw error;
  }
};

export const deleteSubscription = async (id) => {
  try {
    const response = await API.delete(`/subscriptions/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting subscription:", error);
    throw error;
  }
};

export const approveSubscription = async (id) => {
  try {
    const response = await API.put(`/subscriptions/${id}/approve`);
    return response.data;
  } catch (error) {
    console.error("Error approving subscription:", error);
    throw error;
  }
};

export const rejectSubscription = async (id, reason) => {
  try {
    const response = await API.put(`/subscriptions/${id}/reject`, {
      rejection_reason: reason,
    });
    return response.data;
  } catch (error) {
    console.error("Error rejecting subscription:", error);
    throw error;
  }
};

export const cancelSubscription = async (id, data) => {
  try {
    const response = await API.put(`/subscriptions/${id}/cancel`, data);
    return response.data;
  } catch (error) {
    console.error("Error canceling subscription:", error);
    throw error;
  }
};

export const processPayment = async (data) => {
  try {
    const response = await API.post(`/subscriptions/process-payment`, data);
    return response.data;
  } catch (error) {
    console.error("Error processing payment:", error);
    throw error;
  }
};

export const uploadDocument = async (id, document) => {
  try {
    const response = await API.post(`/subscriptions/${id}/document`, document, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading document:", error);
    throw error;
  }
};

export const getLocationHierarchy = async () => {
  try {
    const response = await API.get("/locations/hierarchy");
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription:", error);
    throw error;
  }
};

export const checkParkingPermission = async (params) => {
  try {
    const response = await API.get("/check-parking-permission", {
      params: params,
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la vérification du stationnement:", error);
    throw error;
  }
};


/**
 * Assigns a vehicle UUID to an active subscription.
 * @param {number|string} subscriptionId - The ID of the subscription.
 * @param {string} uuid - The vehicle UUID to assign.
 * @param {string} prefix - The prefix associated with the UUID.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the API request fails.
 */
export const assignVehicleUuidToSubscription = async (subscriptionId, uuid, prefix) => {
  try {
    const response = await API.put(`/subscriptions/${subscriptionId}/assign-uuid`, {
      uuid,
      prefix,
    });
    return response.data;
  } catch (error) {
    console.error("Error assigning vehicle UUID to subscription:", error);
    // Re-throw the error to be handled by the calling function (e.g., display a message to the user)
    throw error;
  }
};

/**
 * Renews an active subscription.
 * @param {number|string} subscriptionId - The ID of the subscription to renew.
 * @returns {Promise<object>} - The response data from the API (including updated subscription and echeances).
 * @throws {Error} - Throws an error if the API request fails.
 */
export const renewSubscription = async (subscriptionId) => {
  try {
    const response = await API.post(`/subscriptions/${subscriptionId}/renew`);
    return response.data;
  } catch (error) {
    console.error("Error renewing subscription:", error);
    throw error;
  }
};

export const processMacaronPayment = async (data) => {
  try {
    const response = await API.post(`/subscriptions/process-macaron-payment`, data);
    return response.data;
  } catch (error) {
    console.error("Error processing macaron payment:", error);
    throw error;
  }
};

export const changeRegistrationFee = async (id, data) => {
  try {
    const response = await API.put(`/subscriptions/${id}/change-registration-fee`, data);
    return response.data;
  } catch (error) {
    console.error("Error changing registration fee:", error);
    throw error;
  }
};

export const changeEcheanceAmount = async (echeanceId, data) => {
  try {
    const response = await API.put(`/echeances/${echeanceId}/change-amount`, data);
    return response.data;
  } catch (error) {
    console.error("Error changing echeance amount:", error);
    throw error;
  }
};

// ─── Data Correction & Override (critical, audited admin actions) ───

/**
 * Reverse a subscription cancellation (canceled → active).
 * @param {number|string} id - Subscription id.
 * @param {string} reason - Mandatory audit reason.
 */
export const reverseCancelSubscription = async (id, reason) => {
  try {
    const response = await API.put(`/subscriptions/${id}/reverse-cancel`, { reason });
    return response.data;
  } catch (error) {
    console.error("Error reversing subscription cancellation:", error);
    throw error;
  }
};

/**
 * Modify an already PAID echeance amount (cascades the delta to payment,
 * transaction and the collecting agent's balance).
 * @param {number|string} echeanceId - Echeance id.
 * @param {{new_amount:number, reason:string, update_agent_solde:boolean}} data
 */
export const modifyPaidEcheance = async (echeanceId, data) => {
  try {
    const response = await API.put(`/echeances/${echeanceId}/modify-paid`, data);
    return response.data;
  } catch (error) {
    console.error("Error modifying paid echeance:", error);
    throw error;
  }
};

/**
 * Aggregated critical-change history for a subscription (across subscription,
 * échéances, payments, payment details, transactions and agents).
 */
export const getSubscriptionCriticalChanges = async (id) => {
  try {
    const response = await API.get(`/subscriptions/${id}/critical-changes`);
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription critical changes:", error);
    throw error;
  }
};

/**
 * Correct a PAID registration_fee / reactivation_fee payment-detail line.
 * @param {number|string} detailId
 * @param {{new_amount:number, reason:string, update_agent_solde:boolean}} data
 */
export const modifyPaidFeeDetail = async (detailId, data) => {
  try {
    const response = await API.put(`/payment-details/${detailId}/modify-paid-fee`, data);
    return response.data;
  } catch (error) {
    console.error("Error modifying paid fee detail:", error);
    throw error;
  }
};

/**
 * Roll back (un-pay) a paid echeance: reverts to pending, soft-deletes the settling
 * payment + details + transaction, and optionally deducts the amount from the agent.
 * @param {number|string} echeanceId
 * @param {{reason:string, update_agent_solde:boolean}} data
 */
export const rollbackEcheancePayment = async (echeanceId, data) => {
  try {
    const response = await API.put(`/echeances/${echeanceId}/rollback-payment`, data);
    return response.data;
  } catch (error) {
    console.error("Error rolling back echeance payment:", error);
    throw error;
  }
};

/**
 * Cancel (rollback) a macaron payment: soft-deletes the payment, its details and the
 * linked transaction, and optionally deducts the amount from the collecting agent's balance.
 * @param {number|string} paymentId
 * @param {{reason:string, update_agent_solde:boolean}} data
 */
export const rollbackMacaronPayment = async (paymentId, data) => {
  try {
    const response = await API.put(`/payments/${paymentId}/rollback-macaron`, data);
    return response.data;
  } catch (error) {
    console.error("Error rolling back macaron payment:", error);
    throw error;
  }
};

export const changeSectionPermission = async (subscriptionId, oldEntityId, newEntityId, reason) => {
  try {
    const response = await API.patch(`/subscriptions/${subscriptionId}/permissions/section`, {
      old_entity: oldEntityId,
      new_entity_id: newEntityId,
      reason: reason,
    });
    return response.data;
  } catch (error) {
    console.error("Error changing section permission:", error);
    throw error;
  }
};